import { useEffect, useState, useRef } from "react";
import { makeStyles } from "tss-react/mui";
import mapboxgl from "mapbox-gl";
import { Grid, Typography, useTheme } from "@mui/material";
import Route from "../../../../common/Route";
import { useStation } from "../../../../providers/StationProvider";
import Surface from "../../../common/core/Surface";
import { useTranslation } from "react-i18next";
import { GeoJsonPoint, GetBounds, GroupByDistance } from "../../../../common/maphelpers";
import { getMissionCauseDescription } from "../../../../providers/MissionProvider";
import { AssumeTaskStateEnum, GetTaskDriverState, GetTaskStateBackgroundColor } from "../../../../common/mission";
import { MapIcons, MissionEnumsLists, TaskEnums } from "../../../../common/constants";
import { NavLink, useNavigate } from "react-router-dom";
import { useEntities } from "@emberly/zenith-client";
import LiveLocationUpdater from "../../../../common/LiveLocationUpdater";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import useIntersectionOnce from "../../../../hooks/useIntersectionOnce";

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      position: "relative",
      width: "100%",
      minHeight: "480px",
      height: "65vh",
      "& .map": {
        position: "absolute",
        width: "100%",
        height: "100%",
        borderRadius: theme.spacing(1)
      },
      "& .mapboxgl-popup": {
        maxWidth: "320px",
        "& .mapboxgl-popup-content": {
          borderRadius: theme.spacing(1),
          padding: theme.spacing(1),
          maxWidth: "320px"
        },
        "& strong": {
          ...theme.typography.subtitle2
        },
        "& table": {
          verticalAlign: "top",
          "& tr": {
            verticalAlign: "top",
          },
          "& td": {
            ...theme.typography.body2
          },
          "& td:first-of-type": {
            color: theme.palette.text.secondary
          },
          "& td:last-of-type": {
            color: theme.palette.text.primary,
            paddingLeft: theme.spacing(0.5)
          }
        },

        "& td, strong": {
          fontFamily: "\"IBM Plex Sans\", Arial",
        }
      }
    },
    title: {
      marginBottom: theme.spacing(2)
    },
    link: {
      textDecoration: "none",
      color: "inherit"
    },
    inlineIcon: {
      marginBottom: "-2px"
    },
  })
);


export default function DashboardMapCard(props) {
  const { missions } = props;
  const { classes } = useStyles();
  const containerRef = useRef(null);
  const theme = useTheme();
  const { station } = useStation();
  const [map, setMap] = useState(null);
  const [liveLocationUpdater, setLiveLocationUpdater] = useState(null);

  const [intersectRef, intersected] = useIntersectionOnce();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const zoomSet = useRef(false);

  const { entities: vehicles } = useEntities("ServiceVehicle");

  useEffect(() => {
    try {

      const component = containerRef.current;
      if (!component || !intersected) return;

      component.innerHTML = "";

      const m = new mapboxgl.Map({
        container: component, // container ID
        style: "mapbox://styles/mapbox/streets-v12", // style URL
        center: station?.location?.coordinates, // starting position [lng, lat]
        zoom: 10.5, // starting zoom
        attributionControl: false
      });

      const live = new LiveLocationUpdater(m);
      zoomSet.current = false;

      m.loadImage(
        "/assets/icons/map/station.png",
        (error, image) => {
          m?.addImage(MapIcons.Common.Station, image, { sdf: true, pixelRatio: 16 });
        }
      );

      m.loadImage(
        "/assets/icons/map/circle.png",
        (error, image) => {
          m?.addImage(MapIcons.Shapes.Circle, image, { sdf: true, pixelRatio: 16 });
        }
      );

      m.loadImage(
        "/assets/icons/map/nav-arrow.png",
        (error, image) => {
          m?.addImage(MapIcons.Navigation.Arrow, image, { sdf: true });
        }
      );

      m.loadImage(
        "/assets/icons/map/car.png",
        (error, image) => {
          m?.addImage(MapIcons.Navigation.Car, image, { sdf: true });
        }
      );

      const ro = new ResizeObserver(() => {
        if (m || m.forceKilled !== true) {
          setTimeout(() => {
            try {
              m?.resize();
            } catch { }
          }, 0);
        }
      });

      ro.observe(component);

      m.on("load", () => {

        m.addSource("mission-locations", {
          type: "geojson",
          data: Route.EmptyGeoJson(),
          cluster: true,
          clusterRadius: 35
        });

        m.addSource("asset-locations", {
          type: "geojson",
          data: Route.EmptyGeoJson()
        });

        // layers def assets
        m.addLayer({
          id: "traffic_warnings_background",
          source: "asset-locations",
          type: "circle",
          paint: {
            "circle-radius": 16, // Adjust the radius as needed
            "circle-color": ["get", "color"], // Set the desired color
            "circle-stroke-width": 2,
            "circle-stroke-color": "#FFF",
          }
        });

        m.addLayer({
          type: "symbol",
          source: "asset-locations",
          id: "traffic_warnings",
          "layout": {
            "icon-image": ["get", "icon"], // Assume you have an icon named "road-works" added to the style
            "icon-size": 1.6,
            "icon-ignore-placement": true,

            "text-allow-overlap": true,
            "text-ignore-placement": true,
            "text-field": ["get", "title"],
            "text-font": [
              "Open Sans Semibold",
              "Arial Unicode MS Bold"
            ],
            "text-size": 12,
            "text-offset": [-0.1, -1.6],
            "text-anchor": "bottom"
          },
          "paint": {
            "icon-color": "#FFF",
            "text-halo-color": "#fff",
            "text-halo-width": 2
          }
        });

        // Layers def mission locations
        m.addLayer({
          id: "mission-locations-widget",
          type: "circle",
          source: "mission-locations",
          filter: ["!", ["has", "point_count"]],
          paint: {
            "circle-radius": 18,
            "circle-color": ["get", "taskColor"],
            "circle-stroke-width": 2,
            "circle-stroke-color": "#000",
          }
        });

        m.addLayer({
          type: "symbol",
          source: "mission-locations",
          id: "mission-locations-widget-text",
          filter: ["!", ["has", "point_count"]],
          layout: {
            "text-allow-overlap": true,
            "text-ignore-placement": true,
            "text-field": ["get", "missionNumber"],
            "text-font": [
              "Open Sans Semibold",
              "Arial Unicode MS Bold"
            ],
            "text-size": 12,
            "text-anchor": "center"
          },
          paint: {
            "text-translate-anchor": "viewport",
            "text-translate": [0, 0]
          }
        });


        m.addLayer({
          type: "symbol",
          source: "mission-locations",
          id: "mission-locations-badge",
          filter: ["!", ["has", "point_count"]],
          layout: {
            "icon-image": MapIcons.Shapes.Circle,
            "icon-size": 1.5,
            "icon-allow-overlap": true,
            "text-allow-overlap": true,
            "text-ignore-placement": true,
            "text-field": ["get", "taskNumber"],
            "text-font": [
              "Open Sans Semibold",
              "Arial Unicode MS Bold"
            ],
            "text-size": 10,
          },
          paint: {
            "icon-translate": [12, 12],
            "icon-translate-anchor": "viewport",
            "icon-color": "#000",
            "text-translate-anchor": "viewport",
            "text-translate": [12, 12],
            "text-color": "#FFFFFF"
          }
        });

        m.addLayer({
          type: "symbol",
          source: "mission-locations",
          id: "mission-locations-text",
          filter: ["!", ["has", "point_count"]],
          layout: {
            "text-allow-overlap": true,
            "text-ignore-placement": true,
            "text-field": ["get", "title"],
            "text-font": [
              "Open Sans Semibold",
              "Arial Unicode MS Bold"
            ],
            "text-size": 12,
            "text-offset": [0, -1.9],
            "text-anchor": "bottom"
          },
          paint: {
            "text-halo-color": "#fff",
            "text-halo-width": 2
          }
        });

        m.addLayer({
          id: "mission-clusters",
          type: "circle",
          source: "mission-locations",
          filter: ["has", "point_count"],
          paint: {

            "circle-stroke-width": 2,
            "circle-stroke-color": "#000",
            "circle-color": [
              "step",
              ["get", "point_count"],
              theme.palette.info.light,
              4,
              theme.palette.info.main,
              9,
              theme.palette.info.dark,
            ],
            "circle-radius": [
              "step",
              ["get", "point_count"],
              20,
              100,
              30,
              750,
              40
            ]
          }
        });

        m.addLayer({
          type: "symbol",
          source: "mission-locations",
          id: "mission-clusters-text",
          filter: ["has", "point_count"],
          layout: {
            "text-field": ["get", "point_count_abbreviated"],
            "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
            "text-size": 12,
          }
        });


        // hover effect
        const popup = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false
        });

        const styles = {
          td: "vertical-align: top; padding-right: 8px;",
          divider: `border: none; height: 1px; background: ${theme.palette.divider}; margin-bottom: 12px;`
        };

        const renderPropertiesToPopup = (props) => {
          // fetch target
          const target = typeof props.target === "string" ? JSON.parse(props.target) : props.target;
          const targetType = target.type;
          const targetDetails = targetType === 1 ? target.carDetails : (targetType === 2 ? target.boatDetails : null);
          const targetDescription = `${!!targetDetails ? `${targetDetails.make || "-"} ${targetDetails.model || "-"}, ${targetDetails.registration || "-"} ` : ""}`;

          // fetch description
          const details = typeof props.details === "string" ? JSON.parse(props.details) : props.details;
          const jobType = details.type === 0 ? "" : t(`mission:enums:type:${MissionEnumsLists.Type[details.type || 0]}`);
          const targetTypeName = target.type === 0 ? "" : t(`mission:enums:targetType:${MissionEnumsLists.TargetType[target?.type || 0]}`);
          const missionDescription = `${jobType} ${targetTypeName}`;

          return (
            `
              <strong>#${props.missionNumber}.${props.taskNumber} ${missionDescription}</strong>
              <table>
                <tr>
                  <td style="${styles.td}">${t("home:cards:map:target")}</td><td style="color: ${theme.palette.text.secondary}; ${styles.td}">${targetDescription}</td>
                </tr>
                <tr>
                  <td style="${styles.td}">${t("home:cards:map:driver")}</td><td style="color: ${theme.palette.text.secondary}; ${styles.td}">${props.driverName || "-"}</td>
                </tr>
                <tr>
                  <td style="${styles.td}">${t("home:cards:map:state")}</td><td style="color: ${theme.palette.text.secondary}; ${styles.td}">${GetTaskDriverState(props.driverAssignedState, props.state, t)}</td>
                </tr>
              </table>
              `
          );
        }

        const onClick = (e) => {
          const props = e.features[0].properties;
          if (!!props.missionId && !!props.taskNumber) {
            navigate(`/activemissions/${props.missionId}/${props.taskNumber}`, { relative: "path" });
          }
        };

        const onMouseEnterPoint = (e) => {
          m.getCanvas().style.cursor = "pointer";

          const { properties } = e.features[0];
          if (!properties) return;

          const coordinates = e.features[0].geometry.coordinates.slice();

          while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
          }

          popup.setLngLat(coordinates).setHTML(renderPropertiesToPopup(properties)).setOffset(26).addTo(m);
        };

        const onMouseEnterCluster = (e) => {
          m.getCanvas().style.cursor = "pointer";

          const { properties, geometry } = e.features[0];
          if (!properties) return;

          const clusterId = properties.cluster_id;
          const point_count = properties.point_count;
          const clusterSource = m.getSource("mission-locations");
          const coordinates = geometry.coordinates.slice();

          clusterSource.getClusterLeaves(clusterId, point_count, 0, (err, aFeatures) => {
            if (err) return;

            popup.setLngLat(coordinates).setHTML(
              aFeatures
                .map(f => renderPropertiesToPopup(f.properties))
                .join(`<hr style="${styles.divider}"/>`)
            ).setOffset(26).addTo(m);

          });
        };

        const onMouseLeave = () => {
          m.getCanvas().style.cursor = "";
          popup.remove();
        };

        m.on("click", "mission-locations-widget", onClick);

        m.on("mouseenter", "mission-locations-widget", onMouseEnterPoint);
        m.on("mouseleave", "mission-locations-widget", onMouseLeave);


        m.on("mouseenter", "mission-clusters", onMouseEnterCluster);
        m.on("mouseleave", "mission-clusters", onMouseLeave);

        setLiveLocationUpdater(live.setup());
        setMap(m);
      });

      return () => {
        m.forceKilled = true;
        setMap(null);
        live.destroy();
        m?.remove();
        ro?.disconnect();
      };
    } catch (err) {
      console.log(err);
    }
  }, [theme, station, containerRef, navigate, t, intersected]);

  useEffect(() => {
    if (!!liveLocationUpdater && !!vehicles) {
      liveLocationUpdater.update(vehicles);
    }
  }, [vehicles, liveLocationUpdater]);

  useEffect(() => {
    if (!map || map.forceKilled) return;

    let missionLocations = [];
    let assetLocations = [];

    if (!!station && station.location?.coordinates?.length >= 2) {

      assetLocations.push(GeoJsonPoint(
        station.location.coordinates,
        {
          title: station.displayName,
          color: theme.palette.success.main,
          icon: MapIcons.Common.Station
        }
      ));

    }

    const groups = GroupByDistance(
      missions.flatMap(m => m.salvageTasks.map(t => ({ ...t, missionId: m.id }))).filter(t => !!t?.route?.waypoints?.length),
      5,
      task => task.route.waypoints[0]?.coordinates
    );

    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];

      for (let j = 0; j < group.length; j++) {
        const task = group[j];
        const mission = missions.find(m => m.id === task.missionId);
        const missionCauseDescription = getMissionCauseDescription(t, mission);
        const location = task.route.waypoints[0];
        const state = AssumeTaskStateEnum(task);
        const driverAssignedState = task.execution.driverAssignedState;

        if (state < TaskEnums.State.Completed && location?.coordinates?.length >= 2) {

          const lon = location.coordinates[0];
          const lat = location.coordinates[1];

          missionLocations.push(GeoJsonPoint(
            [
              lon,
              lat + j * 0.00002,
            ],
            {
              title: missionCauseDescription,

              missionId: mission.id,
              taskNumber: task.number,
              missionNumber: mission.number,

              taskColor: GetTaskStateBackgroundColor(state, driverAssignedState),
              driverName: task.execution?.driver?.name,
              driverAssignedState,
              target: mission.target,
              details: mission.details,
              state
            }
          ));
        }
      }
    }

    map?.getSource("mission-locations")?.setData(Route.GeoJson(missionLocations));
    map?.getSource("asset-locations")?.setData(Route.GeoJson(assetLocations));

    if (missionLocations.length !== 0 && !zoomSet.current) {
      zoomSet.current = true;
      const bounds = GetBounds(missionLocations);

      if (station?.location?.coordinates?.length === 2) {
        bounds.extend(station.location.coordinates);
      }

      if (!!bounds) {
        map?.fitBounds(bounds, { padding: 64, maxZoom: 13 });
      }
    }

  }, [missions, map, theme, station, t]);


  return (
    <Surface xs={12} title={
      <Grid container justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Grid item>
          <Typography variant="h5" className={classes.title}>{t("home:cards:map:title")}</Typography>
        </Grid>
        <Grid item>
          <NavLink to="/map" className={classes.link}>
            <Typography variant="body2" color="info.main" textAlign="center">{t("home:cards:map:link")} <ArrowForwardIcon fontSize="inherit" className={classes.inlineIcon} /></Typography>
          </NavLink>
        </Grid>
      </Grid>
    }>
      <div className={classes.root} ref={intersectRef}>
        <div ref={containerRef} className="map"></div>
      </div>
    </Surface>
  );
}

